import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, AlertCircle, CheckCircle2, Receipt as ReceiptIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import LinkPreview from "@/components/LinkPreview";

const DATE_LOCALES = { pt: ptBR, en: enUS, es };

const TYPES = ["parking", "speeding", "red_light", "toll_violation", "registration", "other"];

const empty = {
  citation_number: "",
  issue_datetime: new Date().toISOString().slice(0, 16),
  amount: 0,
  ticket_type: "parking",
  issuer: "",
  location: "",
  plate: "",
  car_id: "",
  notes: "",
  auto_charge_customer: true,
};

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  charged: "bg-blue-100 text-blue-800 border-blue-300",
  paid: "bg-green-100 text-green-800 border-green-300",
};

export default function Multas() {
  const { t, lang, fmtCurrency } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;
  const [items, setItems] = useState([]);
  const [cars, setCars] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [tk, cs] = await Promise.all([api.get("/tickets"), api.get("/cars")]);
    setItems(tk.data || []);
    setCars(cs.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.car_id) {
      toast.error(t("tickets.carRequired"));
      return;
    }
    try {
      const car = cars.find((c) => c.id === form.car_id);
      const payload = {
        ...form,
        amount: Number(form.amount),
        issue_datetime: new Date(form.issue_datetime).toISOString(),
        car_id: form.car_id,
        plate: car?.plate || null,
      };
      const r = await api.post("/tickets", payload);
      if (r.data.auto_linked) {
        toast.success(t("tickets.createdAutoLinked"));
      } else {
        toast.success(t("tickets.createdNoActiveRental"));
      }
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("tickets.confirmDelete"))) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success(t("tickets.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/tickets/${id}/mark-paid`);
      toast.success(t("tickets.markedPaid"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const total = items.reduce((a, x) => a + Number(x.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="multas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("tickets.pageTitle", { n: items.length, total: fmtCurrency(total) })}</div>
          <p className="text-sm text-zinc-500 mt-1">{t("tickets.pageSubtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => { setForm(empty); setOpen(true); }}
              className="rounded-none bg-[#F5A623] hover:bg-[#D97706]"
              data-testid="add-ticket-button"
            >
              <Plus className="w-4 h-4 mr-2" /> {t("tickets.newTicket")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-none">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">{t("tickets.newTicket")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <Field label={t("rentals.vehicle")} full>
                <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })} required>
                  <SelectTrigger className="rounded-none" data-testid="ticket-car-select">
                    <SelectValue placeholder={t("tickets.selectCarRequired")} />
                  </SelectTrigger>
                  <SelectContent>
                    {cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.model} — {c.plate}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("tickets.issueDatetime")}>
                <Input type="datetime-local" className="rounded-none" required value={form.issue_datetime}
                  onChange={(e) => setForm({ ...form, issue_datetime: e.target.value })}
                  data-testid="ticket-datetime-input" />
              </Field>
              <Field label={t("tickets.amount")}>
                <Input type="number" step="0.01" className="rounded-none" required value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="ticket-amount-input" />
              </Field>
              <LinkPreview carId={form.car_id} datetimeIso={form.issue_datetime} />
              <Field label={t("tickets.citationNumber")}>
                <Input className="rounded-none" required value={form.citation_number}
                  onChange={(e) => setForm({ ...form, citation_number: e.target.value })}
                  data-testid="ticket-citation-input" />
              </Field>
              <Field label={t("tickets.type")}>
                <Select value={form.ticket_type} onValueChange={(v) => setForm({ ...form, ticket_type: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`tickets.types.${tp}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("tickets.issuer")}>
                <Input className="rounded-none" value={form.issuer}
                  onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="City of Boston…" />
              </Field>
              <Field label={t("tickets.location")}>
                <Input className="rounded-none" value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </Field>
              <Field label={t("common.notes")} full>
                <Input className="rounded-none" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
              <div className="col-span-2 flex items-center gap-2 bg-orange-50 border border-[#F5A623]/30 px-3 py-2">
                <input type="checkbox" id="auto-charge" checked={form.auto_charge_customer}
                  onChange={(e) => setForm({ ...form, auto_charge_customer: e.target.checked })} />
                <label htmlFor="auto-charge" className="text-xs">{t("tickets.autoChargeHint")}</label>
              </div>
              <DialogFooter className="col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">
                  {t("common.cancel")}
                </Button>
                <Button type="submit" className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="save-ticket-button">
                  {t("common.save")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <div className="overline mb-2">{t("tickets.emptyTitle")}</div>
          <p className="text-sm text-zinc-500">{t("tickets.emptySub")}</p>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>{t("tickets.citationNumber")}</Th>
                <Th>{t("common.date")}</Th>
                <Th>{t("tickets.type")}</Th>
                <Th>{t("rentals.vehicle")}</Th>
                <Th>{t("rentals.customer")}</Th>
                <Th>{t("tickets.amount")}</Th>
                <Th>{t("common.status")}</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((tk) => (
                <tr key={tk.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td className="font-mono text-xs">{tk.citation_number}</Td>
                  <Td className="font-mono text-xs">{tk.issue_datetime ? format(parseISO(tk.issue_datetime), "dd MMM yy HH:mm", { locale: dateLocale }) : "—"}</Td>
                  <Td>{t(`tickets.types.${tk.ticket_type}`)}</Td>
                  <Td>{tk.car ? <><span className="font-medium">{tk.car.model}</span> <span className="font-mono text-xs text-zinc-500">{tk.car.plate}</span></> : <span className="text-zinc-400 text-xs italic">{t("tickets.notLinked")}</span>}</Td>
                  <Td>{tk.customer?.name || <span className="text-zinc-400 text-xs italic">—</span>}</Td>
                  <Td className="font-mono font-semibold text-red-700">{fmtCurrency(tk.amount)}</Td>
                  <Td>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border ${STATUS_BADGE[tk.status] || STATUS_BADGE.pending}`}>
                      {t(`tickets.status.${tk.status || "pending"}`)}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1">
                      {tk.status !== "paid" && (
                        <button onClick={() => markPaid(tk.id)} title={t("tickets.markPaid")}
                          className="p-1.5 border border-zinc-200 hover:border-green-400 hover:text-green-600">
                          <CheckCircle2 className="w-3 h-3" />
                        </button>
                      )}
                      <button onClick={() => remove(tk.id)}
                        className="p-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 overline">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Field = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <label className="overline">{label}</label>
    {children}
  </div>
);
